/**
 * Date-fns locale mapping constant.
 *
 * Canonical mapping consolidated from:
 * - apps/ssm-web4/src/app/utils/dates/index.js (DATEFNS_LOCALES)
 * - apps/ssm-admin/src/utils/datetime.js (LOCALE_DATES)
 *
 * Both had identical mappings. This is the single source of truth.
 */

import { es, enGB, enUS, de, fr, it, pt, ru, ptBR, da } from 'date-fns/locale';

const ENGLISH_STYLE_LOCALES = new Set(['en-US', 'en-GB', 'th-TH']);

/**
 * Canonical BCP 47 tag: language lower-case, region upper-case.
 * `es-es`, `ES-es` and `es_ES` all become `es-ES`.
 *
 * @param {string} locale
 * @returns {string}
 */
export const normalizeLocaleTag = (locale) => {
    if (typeof locale !== 'string') return locale;

    const trimmed = locale.trim().replace(/_/g, '-');
    if (!trimmed) return '';

    const [language, region, ...rest] = trimmed.split('-');
    if (!language) return '';

    const lang = language.toLowerCase();
    if (!region) return lang;

    return [lang, region.toUpperCase(), ...rest].join('-');
};

/**
 * Locales that use a decimal point and prefix the currency symbol (en-US style).
 *
 * @param {string} locale
 * @returns {boolean}
 */
export const isEnglishStyleLocale = (locale) =>
    ENGLISH_STYLE_LOCALES.has(normalizeLocaleTag(locale));

/**
 * Maps BCP 47 locale tags and short codes to date-fns locale objects.
 * @type {Object<string, import('date-fns').Locale>}
 */
export const DATEFNS_LOCALES = {
    'es-ES': es,
    es: es,
    'en-GB': enGB,
    en: enGB,
    'en-US': enUS,
    'de-DE': de,
    'fr-FR': fr,
    'it-IT': it,
    'pt-PT': pt,
    'ru-RU': ru,
    'pt-BR': ptBR,
    'da-DK': da,
};

/**
 * Look up a locale-keyed map. Normalizes casing first, then tries the language
 * subtag (`es-MX` → `es`) when the exact tag is absent.
 * Does not invent a default locale: unknown tags stay unresolved.
 *
 * @template T
 * @param {Record<string, T>} map
 * @param {string} locale
 * @param {string} [fallbackKey]
 * @returns {T|undefined}
 */
export const resolveLocaleMapValue = (map, locale, fallbackKey) => {
    const tag = normalizeLocaleTag(locale);
    if (!tag || typeof tag !== 'string') {
        return fallbackKey !== undefined ? map[fallbackKey] : undefined;
    }
    const resolved = map[tag] ?? map[tag.split('-')[0]];
    if (resolved !== undefined) return resolved;
    return fallbackKey !== undefined ? map[fallbackKey] : undefined;
};

/**
 * Resolve a date-fns locale, accepting mixed-case and lowercase BCP 47 tags.
 * Unknown tags return `undefined` so date-fns keeps its default (same as before).
 *
 * @param {string} locale
 * @returns {import('date-fns').Locale|undefined}
 */
export const resolveDateFnsLocale = (locale) => resolveLocaleMapValue(DATEFNS_LOCALES, locale);

/**
 * Whether two values fall on the same local calendar day.
 * Accepts Date instances or values parseable by `Date`.
 *
 * @param {Date|string|number} dateA
 * @param {Date|string|number} dateB
 * @returns {boolean}
 */
export const isSameCalendarDay = (dateA, dateB) => {
    if (!dateA || !dateB) return false;

    const a = new Date(dateA);
    const b = new Date(dateB);

    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return false;

    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
};
